import { Alert, Flex, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useStores } from "stores";
import { ProfileHeader } from "./ProfileHeader";
import { getInfoCompanyColumns, getInfoUserColumns, getInfoUserLPColumns } from "./getColumns";

import { ItemInfoBlock } from "components";
import { SquareIcon, WhiteBoxBlock } from "ui";
import { parseErrorMessage } from "utils";
import { getStatusMessage } from "services";
import { BankDetailsProfile } from "components/BankDetails/BankDetailsProfile";
import { AddBankDetailsButton } from "./AddBankDetails";
import { CompanyUserList } from "./CompanyUserList";
import { NotificationSettingsBlock } from "./NotificationSettingsBlock";
import { CustomTabs } from "ui/CustomTabs";
import { ReactComponent as UserIcon } from "images/headerIcons/union.svg";
import { ReactComponent as CompanyIcon } from "images/headerIcons/briefcase.svg";
import styles from "./profile-page.module.css";
import { BankAccountGrid, COMPANY_ROLE_ENUM, CompanyEntity } from "interfaces";
import { SAFE_DEAL } from "utils/envConstants";

enum TABS_KEYS {
  USER_INFO = "userInfo",
  COMPANY_INFO = "companyInfo",
}

export const ProfilePage = observer(() => {
  const { profile, user, companyUsers, companyCard, bankAccountsStore } = useStores();

  const userId = user.userInfo?.id;
  const companyId = user.userInfo?.company_id;

  const { isSeniorLP, isSeniorCustomer, isSystemAdmin, isCustomer, isProvider, isLPRiskManager } = user.roles;

  const isSafeDealMode =
    SAFE_DEAL && profile.company.beneficiaryCreated && profile.company.role === COMPANY_ROLE_ENUM.OPERATOR;

  useEffect(() => {
    if (userId && companyId) {
      profile.fetchProfileById(userId, companyId);
      companyUsers.setId(companyId);
    }
  }, [userId, companyId, profile, companyUsers]);

  const showCompanyUsers = isCustomer || isProvider || isLPRiskManager;
  const showAddBankDetailsButton = (isSeniorCustomer || isSeniorLP || isSystemAdmin) && !isSafeDealMode;

  const tabItems = useMemo(
    () => [
      {
        key: TABS_KEYS.USER_INFO,
        title: "Сведения о пользователе",
        children: (
          <Flex vertical gap="large">
            <WhiteBoxBlock>
              <ItemInfoBlock
                id="profile-user-info"
                withBackground
                oneColumn
                items={getInfoUserColumns({ ...profile.user!, scope: user.userInfo?.scope || [] })}
                blockTitle={
                  <Flex align="center" gap="12px" className="margin-bottom-sm">
                    <SquareIcon icon={UserIcon} size="xl" squareSize="4xl" squareBg />

                    <Typography.Text className="whitebox-title">Сведения о пользователе</Typography.Text>
                  </Flex>
                }
              />
            </WhiteBoxBlock>
            {isSeniorLP && (
              <WhiteBoxBlock>
                <ItemInfoBlock
                  id="profile-user-info"
                  withBackground
                  oneColumn
                  items={getInfoUserLPColumns(profile.company, [], user)}
                />
              </WhiteBoxBlock>
            )}
          </Flex>
        ),
      },
      {
        key: TABS_KEYS.COMPANY_INFO,
        title: "Сведения о компании",
        children: (
          <Flex vertical gap="large">
            <WhiteBoxBlock>
              <ItemInfoBlock
                id="profile-company-info"
                items={getInfoCompanyColumns(profile.company!, user)}
                blockTitle={
                  <Flex align="center" gap="12px" className="margin-bottom-sm">
                    <SquareIcon icon={CompanyIcon} size="xl" squareSize="4xl" squareBg />

                    <Typography.Text className="whitebox-title">Сведения о компании</Typography.Text>
                  </Flex>
                }
              />
            </WhiteBoxBlock>

            <WhiteBoxBlock>
              <Flex align="center" justify="space-between" id="profile-bank-details" className="margin-bottom-md">
                <Typography.Text className="whitebox-title">Банковские реквизиты</Typography.Text>
                {showAddBankDetailsButton && <AddBankDetailsButton />}
              </Flex>
              <BankDetailsProfile />
            </WhiteBoxBlock>

            {showCompanyUsers && <CompanyUserList />}
          </Flex>
        ),
      },
    ],
    [profile.user, profile.company, showCompanyUsers, showAddBankDetailsButton]
  );

  return (
    <Spin spinning={profile.isPending}>
      {profile.fetchStatus === "success" && (
        <Flex vertical gap="32px">
          <ProfileHeader user={profile.user} />

          <Flex gap="large">
            <CustomTabs
              items={tabItems}
              defaultActiveKey={TABS_KEYS.USER_INFO}
              rootClassName={styles.profileTabsContainer}
            />

            <div className={styles.profileNotificationSettingsContainer}>
              <NotificationSettingsBlock />
            </div>
          </Flex>
        </Flex>
      )}
      {profile.fetchStatus === "error" && profile.fetchError && (
        <Alert
          message={getStatusMessage(profile.fetchError.status)}
          description={parseErrorMessage(profile.fetchError)}
          type="error"
          showIcon
        />
      )}
    </Spin>
  );
});
